// Fiche de diagnostic imprimable. Impression → PDF via le navigateur (offline,
// sans dépendance). On écrit la fiche dans #print-area ; le CSS @media print
// masque l'app et n'imprime que cette zone.

import type { DiagnoseResult, Rail } from "../domain/types";
import { esc } from "./dom";

export interface FicheMeta {
  appareil?: string;
  symptom: string;
  technicien?: string;
  reference?: string;
}

function ensureArea(): HTMLElement {
  let area = document.getElementById("print-area");
  if (!area) {
    area = document.createElement("div");
    area.id = "print-area";
    document.body.appendChild(area);
  }
  return area;
}

const stateOrder: Rail["state"][] = ["G3", "always_on", "S5", "S4", "S3", "S0"];

function railsTable(res: DiagnoseResult): string {
  if (!res.board) return "";
  const rows = [...res.board.rails]
    .sort((a, b) => stateOrder.indexOf(a.state) - stateOrder.indexOf(b.state))
    .map(
      (r) => `<tr><td>${esc(r.name)}</td><td>${r.nominal_v} V</td><td>${esc(r.state)}</td>
        <td>${esc(r.source_component ?? "")}</td><td class="meas"></td></tr>`,
    )
    .join("");
  return `<h2>Rails — ${esc(res.board.model)}</h2>
    <table><thead><tr><th>Rail</th><th>Nominal</th><th>État</th><th>Source</th><th>Mesuré</th></tr></thead>
    <tbody>${rows}</tbody></table>`;
}

function checklist(res: DiagnoseResult): string {
  if (!res.symptom_card) return "";
  const items = res.symptom_card.checklist.map((s) => `<li>${esc(s)}</li>`).join("");
  return `<h2>Procédure — ${esc(res.symptom_card.label)}</h2><ol class="check">${items}</ol>`;
}

function causes(res: DiagnoseResult): string {
  if (res.causes.length === 0) return `<p>Aucune correspondance en base.</p>`;
  return `<ol class="causes">${res.causes
    .map((c) => `<li><b>${esc(c.cause)}</b> <span class="pct">${Math.round(c.weight * 100)}%</span>${
      c.procedure ? `<br><span class="proc">${esc(c.procedure)}</span>` : ""
    }</li>`)
    .join("")}</ol>`;
}

function buildHTML(res: DiagnoseResult, meta: FicheMeta): string {
  const date = new Date().toLocaleString("fr-FR");
  const src = res.source === "core" ? "Base régionale (core)" : res.source === "local" ? "Base locale" : "—";
  return `
    <div class="fiche">
      <header class="fiche-head">
        <div><span class="brand">THOR</span> Diagnostique</div>
        <div class="meta">${esc(date)}${meta.reference ? ` · Réf. ${esc(meta.reference)}` : ""}</div>
      </header>
      <table class="kv">
        <tr><th>Appareil</th><td>${esc(meta.appareil ?? res.board?.model ?? "—")}</td></tr>
        <tr><th>Symptôme</th><td>${esc(meta.symptom)}</td></tr>
        <tr><th>Source</th><td>${esc(src)}</td></tr>
        <tr><th>Technicien</th><td>${esc(meta.technicien ?? "")}</td></tr>
      </table>
      <h2>Causes probables</h2>
      ${causes(res)}
      ${railsTable(res)}
      ${checklist(res)}
      <h2>Notes / mesures relevées</h2>
      <div class="notes"></div>
      <footer class="fiche-foot">
        <div>Cause retenue : ____________________________</div>
        <div>Réparé : ☐ oui ☐ non &nbsp;&nbsp; Signature : __________________</div>
        <div class="disclaimer">Pistes indicatives — diagnostic à confirmer par mesures.</div>
      </footer>
    </div>`;
}

export function printFiche(res: DiagnoseResult, meta: FicheMeta): void {
  ensureArea().innerHTML = buildHTML(res, meta);
  window.print();
}
